import React from "react";
import { BackgroundColorI, CellTypeI, TitleColorI, TitleType } from "../Types";
import { getTitleHTMLTag } from "../../helpers/tools";
import Button from "../Inputs/Button";
import SimpleMarkdown from "../SimpleMarkdown/SimpleMarkdown";
import GlobalIcon from "../Icons/GlobalIcon";

interface ComparativeTableI {
  data: {
    BackgroundColor: BackgroundColorI;
    ButtonID: string;
    ButtonLabel: string;
    ButtonURL: string;
    ComparativeColumns: ComparativeColumnsI[];
    HasButton: boolean;
    HasSubtitle: boolean;
    HasTitle: boolean;
    NewTab: boolean;
    SansTitle: string;
    NoFollow: boolean;
    SerifTitle: string;
    Subtitle: string;
    TitleColor: TitleColorI;
    TitleHeading: {
      TitleTypeSelection: TitleType;
    };
    TableMobileMinimumWidth: number;
  };
}

interface ComparativeColumnsI {
  ColumnType: ColumnTypeT;
  ComparativeCells: ComparativeCellsI[];
  HasLabel: boolean;
  HeaderTitle: string;
  HeaderType: CellTypeI;
  ColumnIconType: {
    IconType: any;
  };
  Label: string;
  LogoUrl: string;
  LogoUrlAlt: string;
}

interface ComparativeCellsI {
  ComparativeCellText: string;
  CellType: CellTypeI;
  CellIconType: {
    IconType: any;
  };
  LogoUrl: string;
  LogoUrlAlt: string;
}

type ColumnTypeT = "Default_White" | "Highlighted_Light_Blue";

const ComparativeTable = ({ data }: ComparativeTableI) => {
  const {
    BackgroundColor,
    ButtonID,
    ButtonLabel,
    ButtonURL,
    ComparativeColumns,
    HasButton,
    HasSubtitle,
    HasTitle,
    NewTab,
    SansTitle,
    NoFollow,
    SerifTitle,
    Subtitle,
    TitleColor,
    TitleHeading,
    TableMobileMinimumWidth = 650,
  } = data;

  const backgroundColor = {
    Blue_Gray: "#F5F7FA",
    Light_Blue: "#ECF0FF",
    White: "#FFFFFF",
  }[BackgroundColor.BackgroundColor || "White"];

  const titleColor = {
    Dark_Gray: "darkGray",
    Deep_Life_Blue: "deepLifeBlue",
  }[TitleColor?.TitleColor || "darkGray"];


  const titleC = HasTitle
    ? getTitleHTMLTag(
        <>
          <span className="font-DM font-bold H2">{SansTitle} </span>
          <span className="font-MW font-light H2">{SerifTitle}</span>
        </>,
        TitleHeading?.TitleTypeSelection,
        `text-${titleColor} md:text-center`
      )
    : null;

  const subtitleC = HasSubtitle
    ? getTitleHTMLTag(
        <span className="font-DM font-medium H4">{Subtitle}</span>,
        "p",
        "text-[#424242] md:text-center"
      )
    : null;

  return (
    <section className="overflow-hidden" style={{ backgroundColor }}>
      <div className="flex flex-col max-w-content mx-auto py-[64px] xl:px-[0px] px-[24px] md:gap-[64px] gap-[32px]">
        <div className="flex flex-col md:gap-[16px] gap-[8px]">
          {titleC}
          {subtitleC}
        </div>
        <div
          className="overflow-auto hide-scrollbar rounded-[12px]"
          style={{
            boxShadow:
              "0px 1px 8px 0px rgba(0, 0, 0, 0.10), 0px 3px 3px 0px rgba(0, 0, 0, 0.06), 0px 3px 4px 0px rgba(0, 0, 0, 0.07)",
          }}
        >
          <table
            className="w-full border-separate border-spacing-0 border-none bg-none rounded-[12px]"
            style={{ minWidth: TableMobileMinimumWidth }}
          >
            <thead className="[&>tr>th:not(:last-child):not([data-type])]:border-r border-[#ECECEC]">
              <tr>
                {ComparativeColumns.map((column, idx) => {
                  const { ColumnType } = column;

                  const isFirstCell = idx === 0;
                  const isLastCell = idx === ComparativeColumns.length - 1;
                  const isHighlighted = ColumnType === "Highlighted_Light_Blue";

                  return (
                    <th
                      scope="col"
                      className="py-[16px] px-[12px] h-[84px] H4 font-medium border-b-2 border-b-[var(--primary-color)] md:w-[unset]"
                      style={{
                        textAlign: isFirstCell ? "left" : "center",
                        backgroundColor: isHighlighted ? "#ECF0FF" : "#FFFFFF",
                        color: isHighlighted ? "var(--primary-color)" : "#686868",
                        position: isFirstCell ? "sticky" : "inherit",
                        left: isFirstCell ? "0" : "inherit",
                        borderTopLeftRadius: isFirstCell ? "12px" : "inherit",
                        borderTopRightRadius: isLastCell ? "12px" : "inherit",
                        width: isFirstCell ? "max(40vw, 148px)" : undefined,
                      }}
                      data-type={isHighlighted ? "highlighted" : undefined}
                      key={`header_cell_${idx}`}
                    >
                      <RenderHeaderCellContent {...column} />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="[&>tr>td:not(:last-child):not([data-type])]:border-r border-[#ECECEC]">
              {Array.from(
                Array(ComparativeColumns[0].ComparativeCells.length)
              ).map((_, idx) => {
                return (
                  <tr key={`row_${idx}`}>
                    {ComparativeColumns.map((column, idy) => {
                      const { ColumnType } = column;
                      const CellData = column.ComparativeCells[idx];

                      const IsFirstColumn = idy === 0;
                      const isHighlighted =
                        ColumnType === "Highlighted_Light_Blue";

                      const isFirstCellOfLastRow =
                        idx ===
                          ComparativeColumns[0].ComparativeCells.length - 1 &&
                        idy === 0;
                      const isLastCellOfLastRow =
                        idx ===
                          ComparativeColumns[0].ComparativeCells.length - 1 &&
                        idy === ComparativeColumns.length - 1;

                      return (
                        <td
                          className="py-[16px] px-[12px] BodyL h-[64px] border-b"
                          style={{
                            textAlign: IsFirstColumn ? "left" : "center",
                            fontWeight: IsFirstColumn ? "400" : "500",
                            backgroundColor: isHighlighted
                              ? "#ECF0FF"
                              : "#FFFFFF",
                            color: isHighlighted ? "var(--primary-color)" : "#555555",
                            position: IsFirstColumn ? "sticky" : "inherit",
                            left: IsFirstColumn ? "0" : "inherit",
                            borderBottomLeftRadius: isFirstCellOfLastRow
                              ? "12px"
                              : "inherit",
                            borderBottomRightRadius: isLastCellOfLastRow
                              ? "12px"
                              : "inherit",
                          }}
                          data-type={isHighlighted ? "highlighted" : undefined}
                          key={`cell_${idx}_${idy}`}
                        >
                          <RenderCellContent {...CellData} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {HasButton ? (
          <Button
            id={ButtonID}
            label={ButtonLabel}
            href={ButtonURL}
            noFollow={NoFollow}
            newTab={NewTab}
            secondary={true}
            ghost={true}
            className="!w-fit text-center"
            wrapperClassname="!w-fit mx-auto"
          />
        ) : null}
      </div>
    </section>
  );
};

const Icons = {
  Check: (
    <GlobalIcon
      iconName="CircleCheckFilledIcon"
      size="XS"
      color="var(--primary-color)"
      className="inline-block h-[28px] w-[28px]"
    />
  ),
  X: (
    <GlobalIcon
      iconName="CircleX"
      size="XS"
      color="#E53737"
      className="inline-block h-[28px] w-[28px]"
    />
  ),
};

const RenderHeaderCellContent = ({
  HasLabel,
  HeaderTitle,
  HeaderType,
  ColumnIconType,
  Label,
  LogoUrl,
  LogoUrlAlt,
}: ComparativeColumnsI) => {
  if (HeaderType === "Logo") {
    return <img src={LogoUrl} alt={LogoUrlAlt} className="inline-block" />;
  }
  if (HeaderType === "Icon") {
    return Icons[ColumnIconType.IconType || "Check"];
  }
  if (HeaderType === "Title") {
    return (
      <>
        {HasLabel ? (
          <div className="BodyS font-normal text-[#7B7B7B]">{Label}</div>
        ) : null}
        {HeaderTitle}
      </>
    );
  }
  return null;
};

const RenderCellContent = ({
  ComparativeCellText,
  CellType,
  CellIconType,
  LogoUrl,
  LogoUrlAlt,
}: ComparativeCellsI) => {
  if (CellType === "Logo") {
    return <img src={LogoUrl} alt={LogoUrlAlt} className="inline-block" />;
  }
  if (CellType === "Icon") {
    return Icons[CellIconType.IconType || "Check"];
  }
  if (CellType === "Title") {
    return <SimpleMarkdown>{ComparativeCellText}</SimpleMarkdown>;
  }
  return null;
};

export default ComparativeTable;
